/**
 * A boxed contextual message in the console's status language.
 *
 * Deliberately NOT Carbon's `Callout`/`InlineNotification` component: those throw
 * a runtime a11y invariant when the body contains interactive children (a link
 * or button), and almost every callout here does — a "this center has no doctor
 * row" message links to the center, a removal warning carries a button. So this
 * renders the same visual (kind-coloured accent, status icon, title, body) and
 * accepts arbitrary rich children safely.
 *
 * Used for the things this app exists to make impossible to miss: a center with
 * no doctor row, a validator report that skipped its auth rules, a DNS record on
 * Cloudflare's orange cloud.
 */

import type { ComponentType, ReactNode } from 'react';

import {
  CheckFilledIcon,
  ErrorFilledIcon,
  InfoFilledIcon,
  WarningFilledIcon,
} from './ui/Icon';
import type { Tone } from './Badge';

interface Kind {
  cls: string;
  Icon: ComponentType<{ size?: number | string; className?: string }>;
}

const TONE_KIND: Record<Tone, Kind> = {
  critical: { cls: 'error', Icon: ErrorFilledIcon },
  serious: { cls: 'error', Icon: ErrorFilledIcon },
  warning: { cls: 'warning', Icon: WarningFilledIcon },
  good: { cls: 'success', Icon: CheckFilledIcon },
  info: { cls: 'info', Icon: InfoFilledIcon },
  neutral: { cls: 'info', Icon: InfoFilledIcon },
};

export function Callout({
  tone = 'info',
  title,
  children,
  live = false,
  className,
}: {
  tone?: Tone;
  title: ReactNode;
  children?: ReactNode;
  /** Announce this to assistive tech when it appears mid-session. */
  live?: boolean;
  /** Layout only — `masonry-wide` on a page whose cards are in columns, so an
   *  alert spans them instead of being packed into one. Never for colour: the
   *  tone decides that. */
  className?: string;
}) {
  const { cls, Icon } = TONE_KIND[tone];
  return (
    <div
      className={['app-callout', `app-callout--${cls}`, className ?? '']
        .filter(Boolean)
        .join(' ')}
      role={live ? 'alert' : 'status'}
    >
      <Icon size={20} className="app-callout__icon" />
      <div className="app-callout__body">
        <p className="app-callout__title">{title}</p>
        {children ? <div className="app-callout__content">{children}</div> : null}
      </div>
    </div>
  );
}
